import { useState } from "react";
import { Phone, Mail, MapPin, Clock, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";

export default function Contact() {
  useSeo({
    title: "Contact Valora | NDIS Provider Servicing Australia",
    description: "Get in touch with Valora Support Services — phone, email, contact form. We respond within one business day.",
  });

  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    if (!form.message.trim()) e.message = "Please add a message";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      await api.post("/contact", form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      toast.success("Message sent — we'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { icon: Phone, label: "Phone", value: "0426 635 885", href: "tel:0426635885", testid: "contact-phone" },
    { icon: Mail, label: "Email", value: "info@valorasupportservices.com", href: "mailto:info@valorasupportservices.com", testid: "contact-email" },
    { icon: Clock, label: "Hours", value: "Mon–Sat · 8am–6pm AEST", testid: "contact-hours" },
    { icon: MapPin, label: "Address", value: "13 Appleberry Ave, Coomera QLD 4209 · Servicing all of Australia", testid: "contact-areas" },
  ];

  return (
    <div data-testid="contact-page">
      <section className="bg-gradient-to-b from-[#E6FFFA] via-[#F8FBFA] to-white">
        <div className="container-x py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="eyebrow">Contact us</p>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight text-[#0F2A2C]">
              We're a phone call (or one short message) away.
            </h1>
            <p className="mt-6 max-w-xl text-lg text-[#4A5568]">
              For new enquiries, please use the Referrals form for fastest response. For everything else — partnerships, careers, general questions — we'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-12 lg:py-16 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 space-y-4">
            {cards.map((c, i) => (
              <div key={i} data-testid={c.testid} className="card-soft p-6 flex items-start gap-4">
                <div className="h-11 w-11 rounded-xl bg-[#E6FFFA] text-[#0B6E70] flex items-center justify-center flex-shrink-0">
                  <c.icon className="h-5 w-5" />
                </div>
                <div>
                  <p className="eyebrow">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="mt-1 block text-[17px] font-medium text-[#0F2A2C] hover:text-[#0B6E70]">{c.value}</a>
                  ) : (
                    <p className="mt-1 text-[17px] font-medium text-[#0F2A2C]">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
            <div className="rounded-2xl overflow-hidden aspect-[16/10] border border-[#e6efee]">
              <img src="https://customer-assets.emergentagent.com/job_valora-respite/artifacts/0ydr06h8_image.png" alt="Aerial view of the Gold Coast beach and skyline" className="h-full w-full object-cover" loading="lazy" />
            </div>
          </div>

          <div className="lg:col-span-7">
            {success ? (
              <div data-testid="contact-success" className="rounded-3xl border border-[#7FD1CB] bg-[#E6FFFA] p-10 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-white flex items-center justify-center shadow">
                  <CheckCircle2 className="h-7 w-7 text-[#0B6E70]" />
                </div>
                <h2 className="mt-5 font-serif text-3xl text-[#0F2A2C] font-medium">Thanks for reaching out.</h2>
                <p className="mt-3 text-[#0F2A2C]/80">We'll respond as soon as we can.</p>
                <button type="button" onClick={() => setSuccess(false)} className="mt-7 btn-secondary" data-testid="contact-new-btn">Send another message</button>
              </div>
            ) : (
              <form onSubmit={onSubmit} data-testid="contact-form" className="rounded-3xl border border-[#e6efee] bg-white p-6 sm:p-10 space-y-6">
                <h2 className="font-serif text-2xl lg:text-3xl text-[#0F2A2C] font-medium">Send us a message</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field" htmlFor="cname">Your name *</label>
                    <input id="cname" data-testid="contact-input-name" className="input-field" value={form.name} onChange={(e) => update("name", e.target.value)} />
                    {errors.name && <p className="mt-1 text-xs text-red-600">{errors.name}</p>}
                  </div>
                  <div>
                    <label className="label-field" htmlFor="cemail">Email *</label>
                    <input id="cemail" type="email" data-testid="contact-input-email" className="input-field" value={form.email} onChange={(e) => update("email", e.target.value)} />
                    {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email}</p>}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="label-field" htmlFor="cphone">Phone</label>
                    <input id="cphone" data-testid="contact-input-phone" className="input-field" value={form.phone} onChange={(e) => update("phone", e.target.value)} />
                  </div>
                  <div>
                    <label className="label-field" htmlFor="csubject">Subject</label>
                    <input id="csubject" data-testid="contact-input-subject" className="input-field" value={form.subject} onChange={(e) => update("subject", e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="label-field" htmlFor="cmessage">Message *</label>
                  <textarea id="cmessage" data-testid="contact-input-message" rows={5} className="input-field resize-y" value={form.message} onChange={(e) => update("message", e.target.value)} />
                  {errors.message && <p className="mt-1 text-xs text-red-600">{errors.message}</p>}
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit-btn" className="btn-primary">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : <>Send message <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
